import type { NineForm, NineInput } from "@9amhealth/wcl";
import { NineButton } from "@9amhealth/wcl/generated/react";
import type { ZonedDateTime } from "@internationalized/date";
import type { Dayjs } from "dayjs";
import type { FC } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import date, { DateFormats, dateLocal } from "src/lib/date";
import translate from "src/lib/translate";
import OnEvent from "src/ui/components/OnEvent/OnEvent";
import { FormErrorContext } from "../QuestionnaireStep/FormErrorContext";
import { TextFieldCalendarIcon } from "src/constants/icons";
import DateTimePicker from "src/ui/components/DateTimePicker/DateTimePicker";
import {
  isoDateToWheelDate,
  wheelDateToIsoDate
} from "./MobileDateInputPickerHelpers";

enum DateTypeLabels {
  BIRTH = "dateInput.aria.birth",
  DATE = "dateInput.aria.date"
}

const MobileDateInputPicker: FC<{
  value?: Dayjs | string;
  min?: (d: Dayjs) => Dayjs;
  max?: (d: Dayjs) => Dayjs;
  onChange?: (value: string | undefined) => void;
  error?: string;
  name?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  size?: "full" | "half";
  dateType?: "birth" | "date";
}> = (props) => {
  const inputElRef = useRef<NineInput>(null);
  const [localError, setLocalError] = useState<string>("");
  const [inputKeyUpdateTime, setInputKeyUpdateTime] = useState<number>(0);
  const { min, max, value } = props;
  const lastValidDate = useRef<string>(null);

  const { setFieldError, clearFieldError } = React.useContext(FormErrorContext);

  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    value ? dateLocal(value).format(DateFormats.ISO_YMDHMS) : undefined
  );

  const dateValid = useMemo(() => {
    if (!selectedDate) {
      return false;
    }

    const day = dateLocal(selectedDate, DateFormats.ISO_YMD);
    return day.isValid();
  }, [selectedDate]);

  const [showPicker, setShowPicker] = useState(false);

  // The wheel picker is a poor fit for DOB on a mouse (scrolling ~120 years),
  // so on non-touch devices we hide it and let the user type into the masked
  // input instead. Other date types keep the picker everywhere.
  const isTouchDevice = useMemo(
    () => matchMedia("(pointer:coarse)").matches,
    []
  );
  const enableWheelPicker = isTouchDevice || props.dateType !== "birth";

  const minDate = min
    ? min(dateLocal()).format(DateFormats.ISO_YMD)
    : undefined;

  const maxDate = max
    ? max(dateLocal()).format(DateFormats.ISO_YMD)
    : undefined;

  const dateInRange = useMemo(() => {
    if (!dateValid) {
      return "invalid";
    }
    const day = dateLocal(selectedDate, DateFormats.ISO_YMD);
    const dayString = day.format(DateFormats.ISO_YMD);

    if (minDate && dayString !== minDate && day.isBefore(minDate)) {
      return "before";
    }

    if (maxDate && dayString !== maxDate && day.isAfter(maxDate)) {
      return "after";
    }

    return true;
  }, [selectedDate, dateValid]);

  const getAriaLabel = (): string => {
    switch (props.dateType) {
      case "birth":
        return translate(DateTypeLabels.BIRTH);
      case "date":
      default:
        return translate(DateTypeLabels.DATE);
    }
  };

  const handleWheelDateChange = (wheelDate: ZonedDateTime) => {
    setSelectedDate(wheelDateToIsoDate(wheelDate));
    setInputKeyUpdateTime(Date.now());
  };

  const handleInputChange = (
    event: CustomEvent<{
      value: string;
      maskedValue: string;
      runLocalHandler?: boolean;
    }>
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!event.detail || event.detail.runLocalHandler === false) {
      return;
    }

    const withMask = event.detail.maskedValue;
    const noMask = event.detail.value;
    setLocalError("");

    if (!noMask) {
      setSelectedDate(undefined);
      return;
    }

    const d = dateLocal(withMask, DateFormats.INPUT_US);
    const converted = d.isValid() && d.format(DateFormats.ISO_YMD);

    if (converted) {
      setSelectedDate(converted);

      setTimeout(() => {
        if (props.name) {
          const form = inputElRef.current?.closest("nine-form") as
            | NineForm
            | undefined;
          if (form) {
            form.values[props.name] = withMask;
          }
        }
      }, 60);

      return;
    }

    // Additional validation for month and day
    const [month, day] = withMask.split("/").map(Number);

    if (month < 1 || month > 12) {
      setLocalError(translate("error.invalid.date", { context: "month" }));
      setFieldError(props.name ?? "", "Invalid month");
      setSelectedDate(undefined);
      return;
    }
    if (day < 1 || day > 31) {
      setLocalError(translate("error.invalid.date", { context: "day" }));
      setFieldError(props.name ?? "", "Invalid day");
      setSelectedDate(undefined);
      return;
    }

    const actualNumbersEntered = noMask.replace(/[^0-9]/g, "").length;

    if (actualNumbersEntered !== withMask.length) {
      setLocalError(translate("error.invalid.date", { context: "complete" }));
      setFieldError(props.name ?? "", "Invalid date");
      setSelectedDate(undefined);
      return;
    }
    setLocalError(translate("error.invalid.date"));
    setFieldError(props.name ?? "", "Invalid date");
    setSelectedDate(undefined);
  };

  const inputValue = useMemo(() => {
    const converted = date.convert(
      selectedDate ?? "",
      DateFormats.ISO_YMD,
      DateFormats.INPUT_US
    );

    if (!converted) {
      return "";
    }

    return converted;
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate && dateValid) {
      const day = dateLocal(selectedDate, DateFormats.ISO_YMD);
      const fmt = day.format(DateFormats.ISO_YMD);
      lastValidDate.current = fmt;

      if (dateInRange === "before") {
        const minDateString = dateLocal(minDate).format(
          DateFormats.DISPLAY_US_MDY
        );
        setLocalError(`Date must be after ${minDateString}`);
        setLocalError(
          translate("error.invalid.date", {
            context: "after",
            date: minDateString
          })
        );
        setFieldError(props.name ?? "", `Date must be after ${minDateString}`);
        props.onChange?.(undefined);
        return;
      }

      if (dateInRange === "after") {
        const maxDateString = dateLocal(maxDate).format(
          DateFormats.DISPLAY_US_MDY
        );
        setLocalError(
          translate("error.invalid.date", {
            context: "before",
            date: maxDateString
          })
        );
        setFieldError(props.name ?? "", `Date must be before ${maxDateString}`);
        props.onChange?.(undefined);
        return;
      }

      setLocalError("");
      clearFieldError(props.name ?? "");
      props.onChange?.(fmt);
    }
  }, [selectedDate, dateInRange]);

  const handleInputBlur = () => {
    if (!dateValid) {
      setLocalError("Invalid date");
      setFieldError(props.name ?? "", "Invalid date");
      props.onChange?.(undefined);
    }
    if (selectedDate === undefined && value === undefined) {
      setLocalError("");
      clearFieldError(props.name ?? "");
    }
  };

  return (
    <OnEvent
      events={{
        "nine-change": handleInputChange,
        "nine-blur": handleInputBlur
      }}
    >
      <>
        <nine-input
          pattern="[0-9]*"
          inputMode="numeric"
          mask="00/00/0000"
          lazy={"false"}
          label={props.label}
          name={props.name}
          required={props.required ? "true" : undefined}
          disabled={props.disabled ? "true" : undefined}
          value={inputValue}
          key={`${inputKeyUpdateTime}`}
          force-error={props.error ?? localError}
          size={props.size}
          ref={inputElRef}
          autocomplete={props.dateType === "birth" ? "bday" : "off"}
          aria-label={getAriaLabel()}
        >
          {!props.disabled && enableWheelPicker && (
            <NineButton
              track="false"
              variant="ghost"
              arrow=""
              round="true"
              aria-label={translate("dateInput.aria.open")}
              slot="decoration-end"
              onClick={() => setShowPicker(true)}
            >
              <TextFieldCalendarIcon />
            </NineButton>
          )}
        </nine-input>
        {enableWheelPicker && (
          <DateTimePicker
            show={showPicker}
            onFinish={() => setShowPicker(false)}
            onChange={handleWheelDateChange}
            initialDate={isoDateToWheelDate(selectedDate)}
            title={getAriaLabel()}
            granularity={props.dateType === "birth" ? "ymd" : "day"}
            yearsBack={props.dateType === "birth" ? 120 : undefined}
          />
        )}
      </>
    </OnEvent>
  );
};

export default MobileDateInputPicker;
